import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { EnvelopeOpen } from "@phosphor-icons/react";

// Demo values used to render a live preview of each template.
const SAMPLE_VALUES = {
  timestamp: new Date().toLocaleString("it-IT"),
  triggered_by: "admin@magazzino.it",
  error: "Errore di esempio: connection refused",
  actor_email: "admin@magazzino.it",
  files_uploaded: 3,
  folder_id: "1AbC...xyz",
  product_sku: "IT380FD3",
  product_name: "Router industriale ABC-123",
  product_manufacturer_code: "MFG-001",
  quantity: 5,
  unit_of_measure: "pz",
  supplier_name: "Fornitore Demo S.r.l.",
  urgency: "MEDIA",
  notes: "Serve entro fine settimana",
  requester_name: "Mario Rossi",
  requester_email: "mario@example.com",
  reviewer_name: "Admin Powercom",
  reviewer_email: "admin@magazzino.it",
  review_notes: "OK, procediamo con l'ordine",
  ddt_number: "DDT/2026/00042",
  customer_name: "Cliente Demo S.p.A.",
  customer_email: "cliente@example.com",
  items_table_html:
    "<table cellspacing='0' cellpadding='0' style='border-collapse:collapse;width:100%;margin:12px 0;border:1px solid #e5e5e5'>"
    + "<thead><tr>"
    + "<th style='background:#002FA7;color:#fff;padding:6px 8px;text-align:left;font-size:12px;letter-spacing:.5px;text-transform:uppercase'>SKU</th>"
    + "<th style='background:#002FA7;color:#fff;padding:6px 8px;text-align:left;font-size:12px;letter-spacing:.5px;text-transform:uppercase'>Cod. Produttore</th>"
    + "<th style='background:#002FA7;color:#fff;padding:6px 8px;text-align:left;font-size:12px;letter-spacing:.5px;text-transform:uppercase'>Descrizione</th>"
    + "<th style='background:#002FA7;color:#fff;padding:6px 8px;text-align:left;font-size:12px;letter-spacing:.5px;text-transform:uppercase'>Fornitore</th>"
    + "<th style='background:#002FA7;color:#fff;padding:6px 8px;text-align:left;font-size:12px;letter-spacing:.5px;text-transform:uppercase'>Seriale</th>"
    + "<th style='background:#002FA7;color:#fff;padding:6px 8px;text-align:left;font-size:12px;letter-spacing:.5px;text-transform:uppercase'>Q.tà</th>"
    + "<th style='background:#002FA7;color:#fff;padding:6px 8px;text-align:left;font-size:12px;letter-spacing:.5px;text-transform:uppercase'>U.M.</th>"
    + "</tr></thead>"
    + "<tbody>"
    + "<tr><td style='padding:6px 8px;border-bottom:1px solid #e5e5e5;font-size:13px;font-family:monospace'>IT380FD3</td>"
    + "<td style='padding:6px 8px;border-bottom:1px solid #e5e5e5;font-size:13px;font-family:monospace'>MFG-001</td>"
    + "<td style='padding:6px 8px;border-bottom:1px solid #e5e5e5;font-size:13px'>Router industriale ABC-123</td>"
    + "<td style='padding:6px 8px;border-bottom:1px solid #e5e5e5;font-size:13px'>Fornitore Demo S.r.l.</td>"
    + "<td style='padding:6px 8px;border-bottom:1px solid #e5e5e5;font-size:13px;font-family:monospace'>SN12345678<br>SN12345679</td>"
    + "<td style='padding:6px 8px;border-bottom:1px solid #e5e5e5;font-size:13px;text-align:right'>2</td>"
    + "<td style='padding:6px 8px;border-bottom:1px solid #e5e5e5;font-size:13px'>pz</td></tr>"
    + "<tr><td style='padding:6px 8px;border-bottom:1px solid #e5e5e5;font-size:13px;font-family:monospace'>CAV-UTP-6</td>"
    + "<td style='padding:6px 8px;border-bottom:1px solid #e5e5e5;font-size:13px;font-family:monospace'>—</td>"
    + "<td style='padding:6px 8px;border-bottom:1px solid #e5e5e5;font-size:13px'>Cavo UTP Cat.6 (bulk)</td>"
    + "<td style='padding:6px 8px;border-bottom:1px solid #e5e5e5;font-size:13px'>Fornitore Demo S.r.l.</td>"
    + "<td style='padding:6px 8px;border-bottom:1px solid #e5e5e5;font-size:13px;font-family:monospace'>—</td>"
    + "<td style='padding:6px 8px;border-bottom:1px solid #e5e5e5;font-size:13px;text-align:right'>50</td>"
    + "<td style='padding:6px 8px;border-bottom:1px solid #e5e5e5;font-size:13px'>mt</td></tr>"
    + "</tbody></table>",
};
const renderTpl = (text) =>
  (text || "").replace(/\{\{\s*([\w_]+)\s*\}\}/g, (_, k) => (SAMPLE_VALUES[k] ?? ""));

export default function SettingsTemplates() {
  const [templates, setTemplates] = useState(null);
  const [openTpl, setOpenTpl] = useState(null);
  const [savingTemplates, setSavingTemplates] = useState(false);
  const [tplTestTo, setTplTestTo] = useState({});
  const [tplTesting, setTplTesting] = useState(null);

  // The "DDT emesso" event is now managed in its own tab (Template DDT) with
  // per-customer templates and dedicated recipients. We hide it here to avoid
  // duplication, but leave the backend row untouched for backward compat.
  const HIDDEN_EVENTS = new Set(["movement_signed"]);

  useEffect(() => {
    api.get("/admin/email/templates").then((r) => setTemplates(r.data || {})).catch(() => {});
  }, []);

  const updateTemplate = (evt, patch) =>
    setTemplates((t) => ({ ...t, [evt]: { ...t[evt], ...patch } }));

  const saveTemplates = async () => {
    setSavingTemplates(true);
    try {
      const payload = {};
      for (const [evt, t] of Object.entries(templates || {})) {
        // Skip hidden events: they are managed in dedicated tabs and we
        // don't want their current body to be overwritten by whatever stale
        // state might sit in our local object.
        if (HIDDEN_EVENTS.has(evt)) continue;
        payload[evt] = { subject: t.subject, body_html: t.body_html };
      }
      const { data } = await api.post("/admin/email/templates", { templates: payload });
      setTemplates(data || {});
      toast.success("Template email salvati");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingTemplates(false); }
  };

  const resetTemplate = (evt) => {
    if (!templates?.[evt]) return;
    updateTemplate(evt, {
      subject: templates[evt].default_subject,
      body_html: templates[evt].default_body_html,
    });
  };

  const sendTemplateTest = async (evt) => {
    const to = (tplTestTo[evt] || "").trim();
    if (!to || !to.includes("@")) { toast.error("Indirizzo email destinatario non valido"); return; }
    setTplTesting(evt);
    try {
      const { data } = await api.post("/admin/email/templates/test", {
        event: evt,
        to,
        subject: templates[evt].subject,
        body_html: templates[evt].body_html,
      });
      toast.success(`Email di test inviata a ${to} (${data.provider})`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTplTesting(null); }
  };

  if (!templates) return null;

  return (
    <section className="border border-neutral-200 bg-white" data-testid="templates-section">
      <div className="p-4 md:p-6 border-b border-neutral-200 flex items-start gap-3">
        <EnvelopeOpen size={22} weight="bold" className="text-[#002FA7] mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="label-eyebrow mb-1">Template Email</div>
          <h3 className="font-display font-bold text-lg tracking-tight">Personalizza oggetto e corpo per ogni evento</h3>
          <p className="text-sm text-neutral-600 mt-1">
            Usa <code className="px-1 bg-neutral-100">{"{{"}variabile{"}}"}</code> per inserire i valori dinamici.
            Ogni evento espone variabili diverse — vedi la card sotto ciascun template.
          </p>
        </div>
      </div>

      <div className="p-4 md:p-6">
        <div className="space-y-2" data-testid="notification-templates">
          {Object.entries(templates).filter(([evt]) => !HIDDEN_EVENTS.has(evt)).map(([evt, tpl]) => {
            const isOpen = openTpl === evt;
            return (
              <div key={evt} className="border border-neutral-300">
                <button
                  type="button"
                  data-testid={`tpl-toggle-${evt}`}
                  onClick={() => setOpenTpl(isOpen ? null : evt)}
                  className="w-full flex items-center justify-between px-3 py-2 bg-neutral-50 hover:bg-neutral-100 text-left"
                >
                  <div>
                    <div className="font-medium text-sm">{tpl.label}</div>
                    <div className="text-xs text-neutral-500 font-mono">{evt}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {tpl.is_custom && (
                      <span className="px-1.5 py-0.5 text-[10px] uppercase border border-emerald-400 text-emerald-700 bg-emerald-50">
                        personalizzato
                      </span>
                    )}
                    <span className="text-xs text-neutral-500">{isOpen ? "▲" : "▼"}</span>
                  </div>
                </button>
                {isOpen && (
                  <div className="p-3 space-y-2 border-t border-neutral-200">
                    <div>
                      <label className="label-eyebrow text-neutral-700">Oggetto</label>
                      <input
                        data-testid={`tpl-subject-${evt}`}
                        value={tpl.subject || ""}
                        onChange={(e) => updateTemplate(evt, { subject: e.target.value })}
                        className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                      />
                    </div>
                    <div>
                      <label className="label-eyebrow text-neutral-700">Corpo (HTML)</label>
                      <textarea
                        data-testid={`tpl-body-${evt}`}
                        rows={10}
                        value={tpl.body_html || ""}
                        onChange={(e) => updateTemplate(evt, { body_html: e.target.value })}
                        className="w-full px-3 py-2 border border-neutral-300 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                      />
                    </div>

                    {/* Live preview */}
                    <div className="border border-neutral-300 bg-neutral-50">
                      <div className="px-3 py-1.5 border-b border-neutral-300 bg-neutral-100 flex items-center justify-between">
                        <span className="label-eyebrow text-neutral-700">Anteprima live</span>
                        <span className="text-[10px] text-neutral-500 italic">valori demo per i placeholder</span>
                      </div>
                      <div className="p-3 bg-white">
                        <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1">Oggetto</div>
                        <div
                          data-testid={`tpl-preview-subject-${evt}`}
                          className="text-sm font-medium mb-3 pb-2 border-b border-dashed border-neutral-200"
                        >
                          {renderTpl(tpl.subject)}
                        </div>
                        <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1">Corpo</div>
                        <div
                          data-testid={`tpl-preview-body-${evt}`}
                          className="border border-neutral-200 bg-white p-2 max-h-[400px] overflow-y-auto"
                          dangerouslySetInnerHTML={{ __html: renderTpl(tpl.body_html) }}
                        />
                      </div>
                    </div>
                    <div className="bg-neutral-50 border border-neutral-200 p-2 text-[11px]">
                      <b>Variabili disponibili:</b>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {(tpl.variables || []).map((v) => (
                          <code key={v} className="px-1.5 py-0.5 bg-white border border-neutral-300 font-mono text-[10px]">
                            {"{{"}{v}{"}}"}
                          </code>
                        ))}
                      </div>
                    </div>
                    <div>
                      <button
                        type="button"
                        data-testid={`tpl-reset-${evt}`}
                        onClick={() => resetTemplate(evt)}
                        className="text-xs text-neutral-600 hover:underline"
                      >
                        ↺ Ripristina default
                      </button>
                    </div>

                    {/* Send test email with this draft */}
                    <div className="border-t border-neutral-200 pt-2 mt-2">
                      <div className="text-[11px] uppercase tracking-wider text-neutral-600 mb-1">
                        Invia email di test con questo template
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <input
                          data-testid={`tpl-test-to-${evt}`}
                          value={tplTestTo[evt] || ""}
                          onChange={(e) => setTplTestTo({ ...tplTestTo, [evt]: e.target.value })}
                          placeholder="tuo@indirizzo.email"
                          className="flex-1 min-w-[220px] px-3 py-1.5 border border-neutral-300 text-xs focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                        />
                        <button
                          type="button"
                          data-testid={`tpl-test-send-${evt}`}
                          disabled={tplTesting === evt}
                          onClick={() => sendTemplateTest(evt)}
                          className="px-3 py-1.5 border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white text-xs uppercase tracking-wider disabled:opacity-40"
                        >
                          {tplTesting === evt ? "Invio…" : "Invia test"}
                        </button>
                      </div>
                      <div className="text-[10px] text-neutral-500 mt-1">
                        Usa le modifiche non ancora salvate — utile per verificare il rendering su Gmail/Outlook prima di applicare.
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="mt-3">
          <button
            data-testid="templates-save"
            disabled={savingTemplates}
            onClick={saveTemplates}
            className="px-4 py-2 bg-[#002FA7] text-white hover:bg-[#0038CC] disabled:opacity-40"
          >
            {savingTemplates ? "Salvo…" : "Salva template email"}
          </button>
        </div>
      </div>
    </section>
  );
}
